import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Check, ChevronDown, ClipboardList, Clock3, Lock, Mail, PartyPopper, Phone, Trash2, Video, X, XCircle } from "lucide-react";
import { ordersApi } from "../services/api";

// DatePicker Component
const DateTimePicker: React.FC<{
  value: string;
  onChange: (value: string) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  // Generate time slots (30-minute intervals from 8:00 to 18:00)
  const timeSlots = [];
  for (let hour = 8; hour <= 18; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      timeSlots.push(timeString);
    }
  }

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    if (date && selectedTime) {
      onChange(`${date} à ${selectedTime}`);
    }
  };

  const handleTimeChange = (time: string) => {
    setSelectedTime(time);
    if (selectedDate && time) {
      onChange(`${selectedDate} à ${time}`);
    }
  };

  return (
    <div className="space-y-4">
      <label className="mb-2 block text-sm font-medium text-coffee-200">{label}</label>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-coffee-100">Date</label>
          <div className="relative">
            <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-forest-200" aria-hidden="true" />
            <input type="date" min={today} value={selectedDate} onChange={(event) => handleDateChange(event.target.value)} className="w-full rounded-xl border border-cream-200 bg-cream-50 py-3 pl-10 pr-3 text-coffee-200" style={{ colorScheme: "light" }} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-coffee-100">Heure</label>
          <div className="relative">
            <Clock3 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-forest-200" aria-hidden="true" />
            <select value={selectedTime} onChange={(event) => handleTimeChange(event.target.value)} disabled={!selectedDate} className="w-full rounded-xl border border-cream-200 bg-cream-50 py-3 pl-10 pr-3 text-coffee-200">
              <option value="">Choisir l'heure</option>
              {timeSlots.map((time) => <option key={time} value={time}>{time}</option>)}
            </select>
          </div>
        </div>
      </div>
      <p className="text-sm text-coffee-100">Choisissez le moment qui vous convient pour notre échange (10-15 min)</p>
    </div>
  );
};

const productCategories = {
  vanilla: { name: "Vanille de Madagascar", nameEn: "Madagascar Vanilla", options: ["A Gourmet", "A TK", "B Standard", "B US", "C", "Poudre"] },
  coffee: {
    name: "Café",
    nameEn: "Coffee",
    options: ["Robusta Green Beans"],
  },
  spices: {
    name: "Épices",
    nameEn: "Spices",
    options: ["Girofle Premium", "Poivre Noir Sauvage", "Cannelle de Tamatave"],
  },
  cocoa: {
    name: "Cacao",
    nameEn: "Cocoa",
    options: ["Fèves de Cacao Premium"],
  },
  essentialOils: {
    name: "Huiles Essentielles",
    nameEn: "Essential Oils",
    options: [
      "HE Black pepper",
      "HE Niaouli I, II",
      "HE Saro (Mandravasarotra)",
      "HE Ravintsara",
      "HE Eucalyptus Gobulus",
      "HE Lantana camara",
      "HE Tagete",
      "HE Geranium",
      "HE Clou de girofle",
      "HE Griffe de girofle",
      "HE Cinnamomum",
      "HE Ginger",
      "HE Peppercorn",
      "HE Ylang extra II, III",
      "HE Katrafay",
      "HE Romarin",
      "HE Eucalyptus",
      "HE Citronnelle",
      "HE Cyprès",
      "HE Hélichryse gymnocephale",
      "HE Iary (Psiadia Altissima)",
      "HE Palmarosa",
    ],
  },
  legumes: {
    name: "Autres / Légumineuses",
    nameEn: "Others / Legumes",
    options: ["Haricots et Lentilles variés"],
  },
};

// selectedProducts store the French category name (see comment near its use
// below) so admin-facing data stays consistent regardless of site language.
// This translates it back to the current language for display only.
function categoryDisplayName(frenchName: string, isEn: boolean): string {
  if (!isEn) return frenchName;
  const entry = Object.values(productCategories).find((c) => c.name === frenchName);
  return entry ? entry.nameEn : frenchName;
}

const createContactFormSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .min(2, t("form.validation.nameRequired"))
      .max(50, t("form.validation.nameTooLong")),

    email: z.string().email(t("form.validation.emailInvalid")),

    phone: z.string().optional(),

    selectedProducts: z
      .array(
        z.object({
          category: z.string(),
          option: z.string(),
          quantity: z.string().optional(),
          quantityUnit: z.string().optional(),
        }),
      )
      .min(0),

    destinationAddress: z.string().optional(),
    destinationPostalCode: z.string().optional(),
    destinationCountry: z.string().optional(),
    purchasePeriodStart: z.string().optional(),
    purchasePeriodEnd: z.string().optional(),

    message: z
      .string()
      .max(1000, t("form.validation.messageTooLong"))
      .optional(),

    contactMethod: z.enum(["email", "phone", "video"]),

    availability: z.string().optional(),

    appointmentDateTime: z.string().optional(),

    otherSpecifications: z.string().optional(),
  });

type ContactFormData = z.infer<ReturnType<typeof createContactFormSchema>>;

interface QuoteFormProps {
  onClose: () => void;
}

const QuoteForm: React.FC<QuoteFormProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<
    Array<{
      category: string;
      option: string;
      quantity: string;
      quantityUnit: string;
    }>
  >([]);
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  const contactFormSchema = createContactFormSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      contactMethod: "email",
      selectedProducts: [],
    },
    shouldFocusError: true,
  });

  const watchedContactMethod = watch("contactMethod");

  const addProduct = (category: string, option: string) => {
    const newProduct = {
      category,
      option,
      quantity: "",
      quantityUnit: "kg",
    };
    const updated = [...selectedProducts, newProduct];
    setSelectedProducts(updated);
    setValue("selectedProducts", updated);
  };

  const removeProduct = (index: number) => {
    const updated = selectedProducts.filter((_, i) => i !== index);
    setSelectedProducts(updated);
    setValue("selectedProducts", updated);
  };

  const updateProduct = (index: number, field: string, value: string) => {
    const updated = [...selectedProducts];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedProducts(updated);
    setValue("selectedProducts", updated);
  };

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      // Build product list with quantities
      const products = selectedProducts.map((p) => ({
        name: `${p.category} - ${p.option}`,
        quantity: p.quantity || undefined,
        quantityUnit: p.quantity ? p.quantityUnit || "kg" : undefined,
      }));
      if (data.otherSpecifications) {
        products.push({
          name: data.otherSpecifications,
          quantity: undefined,
          quantityUnit: undefined,
        });
      }
      if (products.length === 0) {
        products.push({
          name: "Non spécifié",
          quantity: undefined,
          quantityUnit: undefined,
        });
      }

      const order = await ordersApi.createPublic({
        customerName: data.name,
        email: data.email,
        phone: data.phone,
        products,
        destinationAddress: data.destinationAddress || undefined,
        destinationPostalCode: data.destinationPostalCode || undefined,
        destinationCountry: data.destinationCountry || undefined,
        purchasePeriodStart: data.purchasePeriodStart || undefined,
        purchasePeriodEnd: data.purchasePeriodEnd || undefined,
        message: data.message,
        contactMethod: data.contactMethod as "email" | "phone" | "video",
        availability: data.appointmentDateTime || data.availability,
        language: (i18n.language.startsWith("en") ? "en" : "fr") as "fr" | "en",
      });

      toast.success(`${t("form.success")} — N° ${order.order_number}`, {
        duration: 6000,
        icon: <PartyPopper className="w-5 h-5 text-forest-200" />,
      });
      reset();
      setSelectedProducts([]);
      onClose();
    } catch (error: any) {
      console.error("Order submission failed", error);

      toast.error(t("form.error"), {
        duration: 4000,
        icon: <XCircle className="w-5 h-5 text-red-500" />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectionCount = (categoryName: string, option: string) =>
    selectedProducts.filter(
      (p) => p.category === categoryName && p.option === option,
    ).length;

  return (
    <div className="flex h-[min(94dvh,860px)] w-full max-w-5xl flex-col overflow-hidden rounded-t-[1.75rem] border border-white/60 bg-cream-50 shadow-2xl shadow-coffee-200/25 sm:h-[min(90dvh,860px)] sm:rounded-[1.75rem]" role="dialog" aria-modal="true" aria-labelledby="quote-form-title">
      {/* Header — hors du scroll pour que le sticky fonctionne */}
      <div className="flex-shrink-0 bg-gradient-to-br from-coffee-200 via-coffee-200 to-forest-200 px-5 py-5 text-cream-50 sm:px-8 sm:py-6">
        <div className="flex justify-between items-center">
          <div className="min-w-0 pr-4">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cream-100/70">Eudora Aromas Madagascar</p>
            <h2 id="quote-form-title" className="font-display text-xl font-bold text-cream-50 sm:text-2xl">
              {t("form.title")}
            </h2>
            <p className="mt-1 max-w-xl text-sm leading-relaxed text-cream-100/80">
              {t("form.subtitle", "Dites-nous ce qu’il vous faut. Nous revenons vers vous rapidement.")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selectedProducts.length > 0 && (
              <span className="hidden items-center gap-1.5 rounded-full bg-cream-50/20 px-3 py-1.5 text-xs font-semibold text-cream-50 sm:flex">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                {selectedProducts.length} {t("form.productWord", "produit")}{selectedProducts.length > 1 ? "s" : ""}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label={t("form.closeAria", "Fermer la fenêtre")}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-cream-50/20 text-cream-100 transition-colors hover:bg-cream-50/15 hover:text-cream-50"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-2" aria-label={t("form.progressAria", "Progression du formulaire")}>
          {[t("form.progressStep1", "Coordonnées"), t("form.progressStep2", "Produits"), t("form.progressStep3", "Échange")].map((step, index) => (
            <div key={step} className="flex items-center gap-2 text-[10px] font-semibold text-cream-100/75 sm:text-xs">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-cream-50/15 text-[10px] text-cream-50">{index + 1}</span>
              <span className="hidden sm:inline">{step}</span>
              {index < 2 && <span className="h-px flex-1 bg-cream-50/20" />}
            </div>
          ))}
        </div>
      </div>

      {/* Contenu scrollable */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-7 p-5 sm:p-8">
          {/* ── Étape 1 : Coordonnées ── */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-forest-200 text-sm font-bold text-cream-50 shadow-soft">
                1
              </span>
              <div><p className="text-[10px] font-bold uppercase tracking-[0.16em] text-forest-200">{t("form.step1Eyebrow", "Étape 1")}</p><h3 className="font-display text-lg font-semibold text-coffee-200">{t("form.step1Title", "Vos coordonnées")}</h3></div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-coffee-200">{t("form.name")} <span className="text-red-500">*</span></label>
                <input {...register("name")} type="text" className="w-full rounded-xl border border-cream-200 bg-white px-3.5 py-2.5 text-sm text-coffee-200" placeholder={t("form.namePlaceholder")} />
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-coffee-200">{t("form.email")} <span className="text-red-500">*</span></label>
                <input {...register("email")} type="email" className="w-full rounded-xl border border-cream-200 bg-white px-3.5 py-2.5 text-sm text-coffee-200" placeholder={t("form.emailPlaceholder")} />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-coffee-200">{t("form.phone")} <span className="font-normal text-coffee-100">({t("form.optional")})</span></label>
                <input {...register("phone")} type="tel" className="w-full rounded-xl border border-cream-200 bg-white px-3.5 py-2.5 text-sm text-coffee-200" placeholder={t("form.phonePlaceholder")} />
              </div>
            </div>
          </div>

          <div className="border-t border-coffee-200/10" />

          {/* ── Étape 2 : Produits — layout deux colonnes ── */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-forest-200 text-sm font-bold text-cream-50 shadow-soft">
                2
              </span>
              <div><p className="text-[10px] font-bold uppercase tracking-[0.16em] text-forest-200">{t("form.step2Eyebrow", "Étape 2")}</p><h3 className="font-display text-lg font-semibold text-coffee-200">{t("form.productsTitle")}</h3></div>
              {selectedProducts.length > 0 && (
                <span className="ml-1 bg-forest-200 text-cream-50 text-xs font-bold px-2 py-0.5 rounded-full">
                  {selectedProducts.length}
                </span>
              )}
            </div>
            <p className="-mt-2 pl-11 text-sm text-coffee-100">
              {t("form.selectCategoryHint", "Cliquez sur une catégorie pour voir les produits disponibles.")}
            </p>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_300px]">
              <div className="space-y-2">
                {Object.entries(productCategories).map(([key, category]) => {
                  // catName (French) is the stable value stored on each
                  // selected product and sent to the backend — admin-facing
                  // data stays consistently French regardless of site
                  // language. catLabel is display-only.
                  const catName = category.name;
                  const catLabel = i18n.language.startsWith("en") ? category.nameEn : category.name;
                  const isOpen = openCategory === key;
                  const countInCategory = selectedProducts.filter(
                    (p) => p.category === catName,
                  ).length;
                  return (
                    <div key={key} className="rounded-xl border border-cream-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpenCategory(isOpen ? null : key)}
                        className="w-full flex items-center justify-between gap-2 px-3.5 py-3 bg-white hover:bg-cream-100/40 transition-colors"
                      >
                          <span className="text-sm font-semibold text-coffee-200">
                            {catLabel}
                          </span>
                          <span className="flex items-center gap-2 flex-shrink-0">
                            {countInCategory > 0 && (
                              <span className="bg-forest-200 text-cream-50 text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                                {countInCategory}
                              </span>
                            )}
                            <ChevronDown className={`h-4 w-4 text-coffee-100 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-3.5 pb-3.5 grid grid-cols-2 md:grid-cols-3 gap-1.5">
                            {category.options.map((option) => {
                              const count = getSelectionCount(catName, option);
                              const selected = count > 0;
                              return (
                                <button
                                  key={option}
                                  type="button"
                                  onClick={() => addProduct(catName, option)}
                                  className={`relative text-left px-2.5 py-2 text-xs border rounded-xl transition-all flex items-center justify-between gap-1 ${
                                    selected
                                      ? "bg-forest-200/12 border-forest-200 text-forest-200 font-medium"
                                      : "border-cream-200 text-coffee-200 hover:bg-forest-200/8 hover:border-forest-200/50"
                                  }`}
                                >
                                  <span className="leading-snug">{option}</span>
                                  {selected && (
                                    <span className="flex-shrink-0 flex items-center gap-0.5">
                                      <Check className="h-3.5 w-3.5 text-forest-200" aria-hidden="true" />
                                      {count > 1 && (
                                        <span className="text-[10px] bg-forest-200 text-cream-50 rounded-full w-4 h-4 flex items-center justify-center font-bold">
                                          {count}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  },
                )}

                {/* Autre produit */}
                <div>
                  <label className="block text-xs font-medium text-coffee-200 mb-1.5">
                    {t("form.otherSpecifications")}
                  </label>
                  <input
                    {...register("otherSpecifications")}
                    type="text"
                    className="w-full px-3.5 py-2.5 border border-cream-200 rounded-xl focus:ring-2 focus:ring-forest-200 focus:border-transparent transition-colors bg-white text-coffee-200 text-sm"
                    placeholder={t("form.otherSpecificationsPlaceholder")}
                  />
                </div>
              </div>

              {/* Colonne droite : panier sélection — sticky */}
              <div className="self-start lg:sticky lg:top-0">
                <div
                  className={`rounded-xl border-2 transition-colors ${selectedProducts.length > 0 ? "border-forest-200/30 bg-forest-200/5" : "border-cream-200 bg-cream-100/40"}`}
                >
                  <div className="px-4 py-3 border-b border-cream-200 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-coffee-200">
                      {t("form.selectedProducts")}
                    </h4>
                    {selectedProducts.length > 0 && (
                      <span className="bg-forest-200 text-cream-50 text-xs font-bold px-2 py-0.5 rounded-full">
                        {selectedProducts.length}
                      </span>
                    )}
                  </div>

                  {selectedProducts.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <ClipboardList className="mx-auto mb-2 h-8 w-8 text-cream-200" strokeWidth={1.5} aria-hidden="true" />
                      <p className="text-xs text-coffee-100">
                        {t("form.emptyCartHint", "Cliquez sur un produit pour l'ajouter")}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-cream-200 max-h-[420px] overflow-y-auto">
                      {selectedProducts.map((product, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.18 }}
                          className="p-3 space-y-2"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-coffee-200 truncate">
                                {product.option}
                              </p>
                              <p className="text-[10px] text-coffee-100 truncate">
                                {categoryDisplayName(product.category, i18n.language.startsWith("en"))}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeProduct(index)}
                              className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 text-red-500 hover:bg-red-200 flex items-center justify-center transition-colors"
                            >
                              <Trash2 className="h-3 w-3" aria-hidden="true" />
                            </button>
                          </div>

                          {/* Quantité + unité */}
                          <div className="grid grid-cols-[1fr_72px] border border-cream-200 rounded-xl overflow-hidden">
                            <input
                              type="number"
                              min="0"
                              step="any"
                              placeholder={t("form.quantity")}
                              value={product.quantity}
                              onChange={(e) =>
                                updateProduct(index, "quantity", e.target.value)
                              }
                              className="w-full px-2.5 py-1.5 text-xs bg-white text-coffee-200 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-forest-200"
                            />
                            <select
                              value={product.quantityUnit}
                              onChange={(e) =>
                                updateProduct(
                                  index,
                                  "quantityUnit",
                                  e.target.value,
                                )
                              }
                              className="w-full px-1.5 py-1.5 border-l border-cream-200 text-xs bg-cream-50 text-coffee-200 cursor-pointer focus:outline-none text-center"
                            >
                              <option value="g">g</option>
                              <option value="kg">kg</option>
                              <option value="tonne">t</option>
                              <option value="lb">lb</option>
                              <option value="mL">mL</option>
                              <option value="L">L</option>
                            </select>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Livraison & période — une seule fois pour toute la commande */}
            <div className="bg-cream-100/50 rounded-xl p-4 space-y-3">
              <h4 className="text-xs font-semibold text-coffee-200 uppercase tracking-wide">
                {t("form.deliveryTitle", "Livraison & période souhaitée")}{" "}
                <span className="text-coffee-100 font-normal normal-case">
                  ({t("form.optional")})
                </span>
              </h4>
              <input
                {...register("destinationAddress")}
                type="text"
                placeholder={t("form.destinationAddressPlaceholder")}
                className="w-full px-3.5 py-2.5 border border-cream-200 rounded-xl focus:ring-2 focus:ring-forest-200 focus:border-transparent transition-colors bg-white text-coffee-200 text-sm"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  {...register("destinationPostalCode")}
                  type="text"
                  placeholder={t("form.destinationPostalCodePlaceholder")}
                  className="w-full px-3.5 py-2.5 border border-cream-200 rounded-xl focus:ring-2 focus:ring-forest-200 focus:border-transparent transition-colors bg-white text-coffee-200 text-sm"
                />
                <input
                  {...register("destinationCountry")}
                  type="text"
                  placeholder={t("form.destinationCountryPlaceholder")}
                  className="w-full px-3.5 py-2.5 border border-cream-200 rounded-xl focus:ring-2 focus:ring-forest-200 focus:border-transparent transition-colors bg-white text-coffee-200 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-coffee-100 mb-1">
                    {t("form.purchasePeriodStart")}
                  </label>
                  <input
                    {...register("purchasePeriodStart")}
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-3.5 py-2.5 border border-cream-200 rounded-xl focus:ring-2 focus:ring-forest-200 focus:border-transparent transition-colors bg-white text-coffee-200 text-sm"
                    style={{ colorScheme: "light" }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-coffee-100 mb-1">
                    {t("form.purchasePeriodEnd")}
                  </label>
                  <input
                    {...register("purchasePeriodEnd")}
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-3.5 py-2.5 border border-cream-200 rounded-xl focus:ring-2 focus:ring-forest-200 focus:border-transparent transition-colors bg-white text-coffee-200 text-sm"
                    style={{ colorScheme: "light" }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-coffee-200/10" />

          {/* ── Étape 3 : Message ── */}
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-forest-200 text-sm font-bold text-cream-50 shadow-soft">
                3
              </span>
              <div><p className="text-[10px] font-bold uppercase tracking-[0.16em] text-forest-200">{t("form.step3Eyebrow", "Étape 3")}</p><h3 className="font-display text-lg font-semibold text-coffee-200">{t("form.contactMethodTitle")}</h3></div>
            </div>

            <textarea
              {...register("message")}
              rows={3}
              className="w-full px-3.5 py-2.5 border border-cream-200 rounded-xl focus:ring-2 focus:ring-forest-200 focus:border-transparent transition-colors resize-none bg-white text-coffee-200 text-sm"
              placeholder={t("form.messagePlaceholder")}
            />
            {errors.message && (
              <p className="text-red-500 text-xs mt-1">
                {errors.message.message}
              </p>
            )}

            {/* Mode de contact */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {(["email", "phone", "video"] as const).map((method) => (
                <label
                  key={method}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 border-2 rounded-xl cursor-pointer transition-all ${
                    watchedContactMethod === method
                      ? "border-forest-200 bg-forest-200/8 text-forest-200"
                      : "border-cream-200 text-coffee-200 hover:border-forest-200/40"
                  }`}
                >
                  <input
                    {...register("contactMethod")}
                    type="radio"
                    value={method}
                    className="sr-only"
                  />
                  {method === "email" && <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />}
                  {method === "phone" && <Phone className="h-4 w-4 shrink-0" aria-hidden="true" />}
                  {method === "video" && <Video className="h-4 w-4 shrink-0" aria-hidden="true" />}
                  <span className="text-sm font-medium">
                    {method === "email"
                      ? t("form.contactEmail")
                      : method === "phone"
                        ? t("form.contactPhone")
                        : t("form.contactVideo")}
                  </span>
                  {watchedContactMethod === method && <Check className="ml-auto h-3.5 w-3.5 text-forest-200" aria-hidden="true" />}
                </label>
              ))}
            </div>

            {watchedContactMethod === "video" && (
              <p className="text-xs text-coffee-100 bg-cream-100 rounded-xl px-3 py-2">
                {t("form.videoExplanation")}
              </p>
            )}
          </div>

          {/* ── Submit ── */}
          <div className="sticky bottom-0 z-10 -mx-5 space-y-3 border-t border-coffee-200/10 bg-cream-50/95 px-5 pb-[env(safe-area-inset-bottom,0px)] pt-4 backdrop-blur-sm sm:-mx-8 sm:px-8">
            <p className="text-xs text-coffee-100 italic text-center">
              {t("form.reassuranceQuote", "Réponse personnalisée, sans engagement.")}
            </p>

            <button
              type="submit"
              disabled={isSubmitting}
              className="mx-auto px-6 py-2 bg-forest-200 text-cream-50 rounded-full hover:bg-forest-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold text-sm flex items-center justify-center gap-2 shadow-soft"
            >
              {isSubmitting ? (
                <>
                  <Clock3 className="h-4 w-4 animate-spin text-cream-50" aria-hidden="true" />
                  {t("form.sending")}
                </>
              ) : (
                <>
                  {t("form.submit")}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </>
              )}
            </button>

            <p className="text-xs text-coffee-100 text-center flex items-center justify-center gap-1.5">
              <Lock className="w-3.5 h-3.5 flex-shrink-0" />
              {t("form.confidentialityNote")}
            </p>
            <p className="text-xs text-coffee-100 text-center italic">
              {t("form.signature")}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteForm;
